import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Modal, Form, Button, Label, Icon, TextArea, Divider, Dropdown } from 'semantic-ui-react'
import PropTypes from "prop-types";
import { HotelDateParameter } from '../../api/services/next';
import { GetCalendarEvents } from '../../api/services/organization';
import { onSuccess, onError, onWarning } from '../shared/messages'
import WeekDaysSelect from '../shared/WeekDaysSelect'
import { DatePicker, TimePicker } from '../shared/DatePicker';
import { useTextResources, useMessageResources } from '../../language';
import { isMobileOnly } from 'mobile-device-detect';
import moment from 'moment';
import { useLoader } from '../../hooks/useLoader';
import { dateFormat } from '../shared/constants';
import HotelsDropDown from '../shared/HotelsDropDown';

const BulkUpdateHotelDateParameterPopup = ({ open, onClose, onSave, controller, ...rest }) => {
    const text = useTextResources()
    const message = useMessageResources()
    const loader = useLoader()
    const [dates, setDates] = useState([new Date(), new Date()])
    const [isOpen, setIsOpen] = useState(open);
    const [days, setDays] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedDates, setSelectedDates] = useState([])
    const [selectedHotelIds, setSelectedHotelIds] = useState([]);
    
    // Fields for update
    const [eta, setEta] = useState(null);
    const [etd, setEtd] = useState(null);
    const [calendarEventId, setCalendarEventId] = useState(null);
    const [notes, setNotes] = useState('');
    const [dateTypeId, setDateTypeId] = useState(null);
    
    // Calendar events data
    const [calendarEvents, setCalendarEvents] = useState([]);

    // Sync internal open state with prop
    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    // Load calendar events when popup opens
    useEffect(() => {
        const loadCalendarEvents = async () => {
            try {
                const events = await GetCalendarEvents({});
                setCalendarEvents(events || []);
            } catch (error) {
                console.error('Failed to load calendar events', error);
            }
        };
        if (isOpen) loadCalendarEvents();
    }, [isOpen]);

    // Date Type options with translations
    const dateTypeOptions = useMemo(() => [
        { key: 1, value: 1, text: text('Regular') },
        { key: 2, value: 2, text: text('Fair') }
    ], [text]);

    // Calendar event dropdown options
    const calendarEventOptions = useMemo(() => 
        calendarEvents.map(e => ({
            key: e.Id,
            value: e.Id,
            text: e.Name
        })), [calendarEvents]);

    // Store callback from controller for AngularJS integration
    const [controllerOnSave, setControllerOnSave] = useState(null);

    // Controller pattern for AngularJS integration
    useEffect(() => {
        if (controller) {
            window[controller] = async (action, params) => {
                switch (action) {
                    case "open":
                        if (params) {
                            if (params.StartDate && params.EndDate) {
                                setDates([params.StartDate, params.EndDate])
                            }
                            if (params.HotelID) {
                                setSelectedHotelIds([params.HotelID])
                            }
                            if (params.onSave) {
                                setControllerOnSave(() => params.onSave)
                            }
                        }
                        setIsOpen(true)
                        break;
                    default:
                        break;
                }
            }
        }
    }, [controller])

    useEffect(() => {
        if (dates[1] && moment(dates[1]).isBefore(moment(dates[0]))) {
            setDates(prevDates => [prevDates[0], prevDates[0]])
        }
    }, [dates])

    const checkIfValid = () => {
        const missingFields = []

        if (selectedDates.length === 0) {
            missingFields.push(text("DateRange"))
        }

        if (!selectedHotelIds || selectedHotelIds.length === 0) {
            missingFields.push(text("Hotel"))
        }

        if (missingFields.length > 0) {
            onWarning(text("MissingFields").replace('{0}', missingFields.join(", ")));
            return false;
        }

        // Check if at least one field is specified for update
        const hasFieldsToUpdate = eta != null || etd != null || calendarEventId != null || notes || dateTypeId != null;
        if (!hasFieldsToUpdate) {
            onWarning(message("AtLeastOneFieldRequired"));
            return false;
        }

        return true;
    }

    const handleSave = async (shouldCloseAfterSave = false) => {
        const isValid = checkIfValid();
        if (!isValid) {
            return;
        }

        try {
            setIsSaving(true);
            loader(true)
            
            // Build date ranges from selected dates, falling back to the currently selected picker range
            const dateRangeSource = selectedDates.length > 0
                ? selectedDates
                : dates?.[0] && dates?.[1]
                    ? [{ startDate: dates[0], endDate: dates[1] }]
                    : [];

            const dateRanges = dateRangeSource.map(d => ({
                FromDate: d.startDate,
                ToDate: d.endDate
            }));

            const request = {
                HotelIds: selectedHotelIds,
                DateRanges: dateRanges,
                Weekdays: days,
                DefaultCheckinTime: eta || undefined,
                DefaultCheckoutTime: etd || undefined,
                CalendarEventId: calendarEventId || undefined,
                Notes: notes || undefined,
                DateTypeId: dateTypeId || undefined
            };

            const result = await HotelDateParameter.BulkUpdate(request);

            if (result.Success) {
                onSuccess(text("SavedSuccessfully"));
                onSave?.();
                controllerOnSave?.();
                if (shouldCloseAfterSave) {
                    handleClose();
                }
            } else {
                onWarning(result.Errors?.join(', ') || text("SaveFailed"));
            }
        } catch (error) {
            console.error(error)
            error.status === 400 ? onWarning(error) : onError(error)
        } finally {
            loader(false)
            setIsSaving(false);
        }
    }

    const resetFields = useCallback(() => {
        setSelectedDates([])
        setDates([new Date(), new Date()])
        setSelectedHotelIds([])
        setDays(null)
        setEta(null)
        setEtd(null)
        setCalendarEventId(null)
        setNotes('')
        setDateTypeId(null)
    }, [])

    const resetFieldsRef = useRef(resetFields)
    resetFieldsRef.current = resetFields

    const handleClose = () => {
        setIsOpen(false)
        resetFields()
        onClose?.()
    }

    useEffect(() => {
        if (!isOpen) {
            resetFieldsRef.current();
        }
    }, [isOpen]);

    const addDateRange = () => {
        if (selectedDates.some((d) => moment(d.startDate).isSame(dates[0], 'day') && moment(d.endDate).isSame(dates[1], 'day'))) {
            onWarning(text('WarningDuplicateDates'))
            return
        }

        setSelectedDates(selectedDates => selectedDates.concat({
            startDate: dates[0],
            endDate: dates[1]
        }))
    }

    const removeDateRange = (index) => {
        setSelectedDates(selectedDates => selectedDates.filter((_, i) => i !== index))
    }

    const handleDatesChanged = (newValue) => {
        if (newValue) {
            setDates(newValue)
        } else {
            setDates([new Date(), new Date()])
        }
    }

    const memoDates = useMemo(() => {
        return selectedDates.length ? selectedDates : [{ startDate: dates[0], endDate: dates[1] }]
    }, [dates, selectedDates])

    return (
        <Modal size={isMobileOnly ? "fullscreen" : "small"} open={isOpen} className={isMobileOnly ? "mobile" : ""} style={{ zIndex: 1000000 }} {...rest} >
            <Modal.Header>{text("BulkUpdateHotelDateParameters")}</Modal.Header>
            <Modal.Content scrolling>
                <Form unstackable>
                    {/* Selected Date Ranges */}
                    <Form.Group unstackable widths={'equal'} style={{ flexWrap: "wrap" }}>
                        {selectedDates.map((range, index) => (
                            <Label.Group key={index}>
                                <Label>
                                    {moment(range.startDate).format(dateFormat)}
                                    {' - '}
                                    {moment(range.endDate).format(dateFormat)}
                                    <Icon name="delete" onClick={() => removeDateRange(index)} />
                                </Label>
                            </Label.Group>
                        ))}
                    </Form.Group>

                    {/* Date Range Picker with Add button */}
                    <Form.Group unstackable style={{ alignItems: "flex-end" }}>
                        <Form.Field width={12} style={{ display: "grid" }}>
                            <DatePicker
                                picktype='range'
                                onChange={handleDatesChanged}
                                label={text("DateRange")}
                                value={dates}
                            />
                        </Form.Field>
                        <Form.Field width={4}>
                            <Button primary size="small" onClick={addDateRange}>
                                {text("AddDateRange")}
                            </Button>
                        </Form.Field>
                    </Form.Group>

                    <Divider hidden />

                    {/* Weekday Selector */}
                    <Form.Field 
                        label={text("Weekdays")} 
                        control={WeekDaysSelect} 
                        onChange={setDays} 
                        dates={memoDates}
                    />

                    <Divider hidden />

                    {/* Hotel Multi-Select */}
                    <Form.Field 
                        label={text("Hotel")} 
                        required 
                        control={HotelsDropDown} 
                        multiple
                        value={selectedHotelIds}
                        onChange={setSelectedHotelIds}
                    />

                    <Divider hidden />

                    {/* ETA / ETD */}
                    <Form.Group widths={2}>
                        <Form.Field>
                            <label>{text("DefaultCheckinTime")}</label>
                            <TimePicker
                                value={eta}
                                onChange={setEta}
                                clearable
                            />
                        </Form.Field>
                        <Form.Field>
                            <label>{text("DefaultCheckoutTime")}</label>
                            <TimePicker
                                value={etd}
                                onChange={setEtd}
                                clearable
                            />
                        </Form.Field>
                    </Form.Group>

                    {/* Calendar Event */}
                    <Form.Field>
                        <label>{text("CalendarEvent")}</label>
                        <Dropdown
                            selection
                            clearable
                            search
                            placeholder={text("SelectCalendarEvent")}
                            value={calendarEventId}
                            onChange={(e, { value }) => setCalendarEventId(value || null)}
                            options={calendarEventOptions}
                        />
                    </Form.Field>

                    {/* Date Type Dropdown */}
                    <Form.Field>
                        <label>{text("DateType")}</label>
                        <Dropdown
                            selection
                            clearable
                            placeholder={text("SelectDateType")}
                            value={dateTypeId}
                            onChange={(e, { value }) => setDateTypeId(value || null)}
                            options={dateTypeOptions}
                        />
                    </Form.Field>

                    {/* Notes */}
                    <Form.Field>
                        <label>{text("Notes")}</label>
                        <TextArea
                            value={notes}
                            onChange={(e, { value }) => setNotes(value)}
                        />
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button size="small" negative onClick={handleClose}>{text("Close")}</Button>
                <Button size="small" positive loading={isSaving} onClick={() => handleSave(true)}>{text("Saveandclose")}</Button>
                <Button size="small" positive loading={isSaving} onClick={() => handleSave(false)}>{text("Save")}</Button>
            </Modal.Actions>
        </Modal>
    )
}

BulkUpdateHotelDateParameterPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    controller: PropTypes.string
}

export default BulkUpdateHotelDateParameterPopup
